import React, { FC, useMemo, useState } from 'react';
import { App } from '../../../models/app';
import { MenuOptionCallBack, OverFlowMenu, OverflowMenuOption } from '../../Common/OverFlowMenu/OverFlowMenu';
import { UninstallAppButton } from '../AppActions/uninstall';
import { AppRelatedTranslationKeys } from '../../../pages/AppDetails/types';
import { AppActionPostRequestResponseHandler } from '../AppActions/types';
import { useTranslation } from 'react-i18next';
import { OpenAppButton } from '../AppActions/open';
import styles from './styles.module.scss';
import { appHasRatingsAndReviewsPermission, canCreateNewReview } from '../../../component-helpers/app-reviews';
import { useFetchAppReviewsQuery } from '../../../redux/api/appMarket-api-slice';
import { usePermission } from '../../../hooks/usePermission';
import { AppMarketPermissions } from '../../../models/enums';
import { canShowChangeSubscription, canUninstallApp, isOpenAppDisabled } from '../../../component-helpers/app-actions';
import { useMerchantDelinquencyStatus } from '../../../hooks/useMerchantDelinquencyStatus';
import { useRatingsAndReviews } from '../../../contexts/providers/ratings-reviews';

export enum AppOverFlowMenuScenario {
  AppDetails,
  MyApps
}

export enum AppOverFlowItem {
  ChangeSubscription,
  OpenApp,
  RateApp,
  Uninstall,
}

export type AppOverFlowMenuCallBacks = Record<AppOverFlowItem, (() => void) | AppActionPostRequestResponseHandler | undefined>

interface ComponentProps {
  app: App;
  callbacks: AppOverFlowMenuCallBacks;
  page?: AppOverFlowMenuScenario;
}

export const AppOverFlowMenu: FC<ComponentProps> = (
  {
    app,
    page = AppOverFlowMenuScenario.AppDetails,
    callbacks,
  }) => {
  const { id: appId } = app;
  const { t } = useTranslation();
  const isMerchantDelinquent = useMerchantDelinquencyStatus();
  const openAppDisabled = isOpenAppDisabled(app, isMerchantDelinquent);
  const [menuOpen, setMenuIsOpen] = useState<boolean>(false);
  const ratingsAndReviewsContext = useRatingsAndReviews();
  const { data: appReviews, isLoading: reviewsLoading } = useFetchAppReviewsQuery({ appId, limit: 1, offset: 0 }, {skip: !app.installed || !menuOpen}); // Fetch first review to see if the review has been created already
  const { enabled: hasInstallUninstallPermission } = usePermission(AppMarketPermissions.InstallUninstallApps);
  const overFlowMenuOptions = useMemo((): OverflowMenuOption[] => {
    let options: OverflowMenuOption[] = [];
    if (!app.installed) return options;
    const OverFlowMenuItemsMap: Record<AppOverFlowItem, OverflowMenuOption> = {
      [AppOverFlowItem.Uninstall]: {
        key: 'item_uninstall',
        renderItem: () => <UninstallAppButton
          actionCallBack={callbacks[AppOverFlowItem.Uninstall]}
          app={app}
          isMenuItem={true}
          text={t('buttons.uninstall_app')}
        />,
        optionClickCallBack: undefined,
      },
      [AppOverFlowItem.ChangeSubscription]: {
        key: 'item_change',
        renderItem: () => <div aria-label={`${t(AppRelatedTranslationKeys.ChangeSubscription)} ${t('aria.opens_modal')}`}>{t(AppRelatedTranslationKeys.ChangeSubscription)}</div>,
        optionClickCallBack: callbacks[AppOverFlowItem.ChangeSubscription] as MenuOptionCallBack,
      },
      [AppOverFlowItem.RateApp]: {
        key: 'item_rate',
        renderItem: () => <div aria-label={`${t(AppRelatedTranslationKeys.RateApp)} ${t('aria.opens_modal')}`}>{t(AppRelatedTranslationKeys.RateApp)}</div>,
        optionClickCallBack: callbacks[AppOverFlowItem.RateApp] as MenuOptionCallBack,
      },
      [AppOverFlowItem.OpenApp]: {
        key: 'item_open',
        renderItem: () => <OpenAppButton app={app} isMenuItem={true} />,
        optionClickCallBack: undefined,
      },
    };

    if (page === AppOverFlowMenuScenario.MyApps && !openAppDisabled) options.push(OverFlowMenuItemsMap[AppOverFlowItem.OpenApp]);
    if (canShowChangeSubscription(app, hasInstallUninstallPermission)) options.push(OverFlowMenuItemsMap[AppOverFlowItem.ChangeSubscription]);
    if (!reviewsLoading && canCreateNewReview(app, appReviews, hasInstallUninstallPermission) && appHasRatingsAndReviewsPermission(ratingsAndReviewsContext, app.id, app.developer.id)) options.push(OverFlowMenuItemsMap[AppOverFlowItem.RateApp]);
    if (canUninstallApp(app, hasInstallUninstallPermission)) options.push(OverFlowMenuItemsMap[AppOverFlowItem.Uninstall]);
    return options;
  }, [app, callbacks, hasInstallUninstallPermission, page, ratingsAndReviewsContext, reviewsLoading, appReviews, t, openAppDisabled]);

  return (
    <>
      {
        overFlowMenuOptions.length > 0 &&  <div className={styles.overFlowMenu}>
          <OverFlowMenu options={overFlowMenuOptions} onOpen={() => setMenuIsOpen(true)}/>
        </div>
      }
    </>
  );
};
